import LBox from "@components/LBox";
import LIcon from "@components/LIcon";
import LLink from "@components/LLink";
import LText from "@components/LText";
import UserAvatar from "@components/UserAvatar";
import { getTodaysTopHitsId, ValidTerritoryCode } from "@libs/utils";
import { useDownloadProgress } from "@utils/downloadManager/hooks";
import { getLibraryItemMainFields } from "@utils/index";
import React from "react";
import { moderateScale } from "@utils/scale";
import { fonts } from "@theme";
import { Art } from "@components/Collection/Art";
import { useMe } from "@hooks/trpc/useMe";
import { getCountryCode } from "@utils/region";
import { getLibraryPlaylistSubtitle } from "@components/LibraryItem.helpers";

interface LibraryItemProps {
  data:
    | {
        id: string;
        type: "album";
        title: string;
        image: string | null;
        artistDisplayText: string;
        privateSongIds: string[];
      }
    | {
        id: string;
        type: "artist";
        name: string;
        image: string | null;
      }
    | {
        id: string;
        type: "playlist";
        title: string;
        images: string[] | null;
        songIds: string[];
        user: {
          id: string;
          name: string;
        };
        visibility: "public" | "private";
        privateSongIds: string[];
      };
}

const LibraryItem = ({ data }: LibraryItemProps) => {
  const { id, type } = data;
  const {
    query: { data: user },
  } = useMe();
  const countryCode =
    user?.plan?.countryId ??
    user?.countryCode ??
    (getCountryCode() as ValidTerritoryCode);

  const subTextGenerator = (own: boolean): string | undefined => {
    switch (type) {
      case "album":
        return data.artistDisplayText;
      case "playlist":
        return getLibraryPlaylistSubtitle({
          id,
          isOwn: own,
          ownerName: data.user?.name ?? "Unknown",
          songCount: data.songIds?.length ?? 0,
          todaysTopHitsId: getTodaysTopHitsId(countryCode),
          visibility: data.visibility,
        });
      case "artist":
        return undefined;
    }
  };

  const downloadStatus = useDownloadProgress(id);

  const { own, title, images } = getLibraryItemMainFields(
    data,
    user ? { uid: user.id } : undefined,
  );

  const subText = subTextGenerator(own);

  if (
    id.startsWith("liked_") &&
    data.type === "playlist" &&
    data.songIds?.length === 0
  ) {
    return null;
  }

  return (
    <LLink
      href={{
        pathname: `/(app)/(tabs)/library/[collectionType]/[collectionId]`,
        params: {
          collectionType: type,
          collectionId: id,
        },
      }}
      sentryEventName={`library_item_${type}_${id}`}
    >
      <LBox style={{ flexGrow: 1 }}>
        <LBox mb="s4" flexDirection="row" alignItems="center">
          <LBox mr="h_s4">
            {type === "artist" ? (
              <UserAvatar
                image={images?.at(0)}
                size={moderateScale(48)}
                imageSize="small"
                borderColor="buttonTextPrimary"
                alignSelf="flex-start"
              />
            ) : (
              <Art
                id={id}
                type={type}
                imageUrl={images}
                imageSize="small"
                size={moderateScale(48)}
                borderRadius={type === "album" ? "s" : "zero"}
                hasPrivateSong={data.privateSongIds?.length > 0}
                privateContentIconSize={16}
                privateContentIconPadding="s1"
                privateSongIds={
                  type === "album" || type === "playlist"
                    ? data.privateSongIds
                    : undefined
                }
              />
            )}
          </LBox>

          <LBox justifyContent="center" gap="s1">
            <LBox flexDirection="row" alignItems="center">
              <LText
                variant="listItem"
                fontFamily={fonts.medium}
                numberOfLines={1}
              >
                {title}
              </LText>
            </LBox>
            {subText ? (
              <LBox flexDirection="row" gap="s1" alignItems="center">
                {downloadStatus === "downloaded" ? (
                  <LIcon name="downloaded" size={12}></LIcon>
                ) : null}
                <LText
                  variant="bodyM"
                  color="subTextGrey"
                  fontSize={13}
                  numberOfLines={1}
                >
                  {subText}
                </LText>
              </LBox>
            ) : null}
          </LBox>
        </LBox>
      </LBox>
    </LLink>
  );
};

export default LibraryItem;
